<template>
  <div
    class="hero-banner critical-component-clear"
    :class="isMobile
      ? `${banner.alignHorizontalMobileValue || ''}`
      : `${banner.alignHorizontalDesktopValue || ''}`"
  >
    <div
      class="hero-banner__container"
      :class=" [
        `text-colour--${banner.heading.textColour}`,
        layoutClass,
        settings.headingWidth === 'full' ? 'full' : 'default',
        settings.background === 'no_background' ? 'no-background' : 'default_background'
      ]"
      role="link"
      tabindex="0"
      @click="handleBannerClick"
      @mouseenter="handleContainerHover"
      @mouseleave="handleContainerLeave"
      @focus="handleContainerHover"
      @blur="handleContainerLeave"
    >
      <div class="hero-banner__media">
        <template v-if="banner.mobileVideo || banner.desktopVideo">
          <video-player
            v-if="banner.mobileVideo"
            class="hero-banner__mobile-video"
            :attributes="banner.videoSettings"
            :image="banner.mobileVideo.preview_image"
            :lazyload="banner.lazyload"
            :loaded-animation="false"
            :ratio="false"
            :intersection-observer="{
              enable: banner.videoSettings.autoplay,
            }"
            sizes="100vw"
            :sources="banner.mobileVideo.sources"
            @click="handleMainMediaClick"
            @mouseenter="(e) => handleMediaHover(e, 'main')"
            @mouseleave="handleMediaLeave"
            @focus="(e) => handleMediaHover(e, 'main')"
            @blur="handleMediaLeave"
          />

          <video-player
            v-if="banner.desktopVideo"
            class="hero-banner__desktop-video"
            :attributes="banner.videoSettings"
            :image="banner.desktopVideo.preview_image"
            :lazyload="banner.lazyload"
            :loaded-animation="false"
            :ratio="settings.ratioDesktop"
            :intersection-observer="{
              enable: banner.videoSettings.autoplay,
            }"
            sizes="100vw"
            :sources="banner.desktopVideo.sources"
            @click="handleMainMediaClick"
            @mouseenter="(e) => handleMediaHover(e, 'main')"
            @mouseleave="handleMediaLeave"
            @focus="(e) => handleMediaHover(e, 'main')"
            @blur="handleMediaLeave"
          />
        </template>

        <!-- Additional Mobile/Desktop Video -->
        <template v-if="banner.additionalMobileVideo || banner.additionalDesktopVideo">
          <video-player
            v-if="banner.additionalMobileVideo"
            class="hero-banner__additional-mobile-video"
            :attributes="banner.videoSettings"
            :image="banner.additionalMobileVideo.preview_image"
            :lazyload="banner.lazyload"
            :loaded-animation="false"
            :ratio="false"
            :intersection-observer="{ enable: banner.videoSettings.autoplay }"
            sizes="100vw"
            :sources="banner.additionalMobileVideo.sources"
            @click="handleAdditionalMediaClick"
            @mouseenter="(e) => handleMediaHover(e, 'additional')"
            @mouseleave="handleMediaLeave"
            @focus="(e) => handleMediaHover(e, 'additional')"
            @blur="handleMediaLeave"
          />
          <video-player
            v-if="banner.additionalDesktopVideo"
            class="hero-banner__additional-desktop-video"
            :attributes="banner.videoSettings"
            :image="banner.additionalDesktopVideo.preview_image"
            :lazyload="banner.lazyload"
            :loaded-animation="false"
            :ratio="settings.ratioDesktop"
            :intersection-observer="{ enable: banner.videoSettings.autoplay }"
            sizes="100vw"
            :sources="banner.additionalDesktopVideo.sources"
            @click="handleAdditionalMediaClick"
            @mouseenter="(e) => handleMediaHover(e, 'additional')"
            @mouseleave="handleMediaLeave"
            @focus="(e) => handleMediaHover(e, 'additional')"
            @blur="handleMediaLeave"
          />
        </template>

        <template
          v-if="banner.mobileImage?.url ||
            banner.desktopImage?.url ||
            banner.additionalMobileImage?.url ||
            banner.additionalDesktopImage?.url
          "
        >
          <art-direction
            v-if="banner.mobileImage?.url && !banner.mobileVideo"
            class="hero-banner__mobile-image"
            :image="banner.mobileImage"
            :image-desktop="banner.mobileImage"
            :ratio="settings.ratio"
            :padding="layoutType === 'layout_1' ? false : true"
            :ratio-desktop="settings.ratio"
            :loaded-animation="false"
            :lazyload="banner.lazyload"
            @click="handleMainMediaClick"
            @mouseenter="(e) => handleMediaHover(e, 'main')"
            @mouseleave="handleMediaLeave"
            @focus="(e) => handleMediaHover(e, 'main')"
            @blur="handleMediaLeave"
          />
          <art-direction
            v-if="banner.desktopImage?.url && !banner.desktopVideo"
            class="hero-banner__desktop-image"
            :image="banner.desktopImage"
            :image-desktop="banner.desktopImage"
            :ratio="settings.ratioDesktop"
            :ratio-desktop="settings.ratioDesktop"
            :loaded-animation="false"
            :lazyload="banner.lazyload"
            :padding="layoutType === 'layout_1' ? false : true"
            @click="handleMainMediaClick"
            @mouseenter="(e) => handleMediaHover(e, 'main')"
            @mouseleave="handleMediaLeave"
            @focus="(e) => handleMediaHover(e, 'main')"
            @blur="handleMediaLeave"
          />
          <!-- Additional Mobile Image -->
          <art-direction
            v-if="banner.additionalMobileImage?.url && !banner.additionalMobileVideo"
            class="hero-banner__additional-mobile-image"
            :image="banner.additionalMobileImage"
            :ratio="settings.ratio"
            :ratio-desktop="settings.ratio"
            :loaded-animation="false"
            :padding="layoutType === 'layout_1' ? false : true"
            :lazyload="banner.lazyload"
            @click="handleAdditionalMediaClick"
            @mouseenter="(e) => handleMediaHover(e, 'additional')"
            @mouseleave="handleMediaLeave"
            @focus="(e) => handleMediaHover(e, 'additional')"
            @blur="handleMediaLeave"
          />
          <!-- Additional Desktop Image -->
          <art-direction
            v-if="banner.additionalDesktopImage?.url && !banner.additionalDesktopVideo"
            class="hero-banner__additional-desktop-image"
            :image="banner.additionalDesktopImage"
            :image-desktop="banner.additionalDesktopImage"
            :ratio="settings.ratioDesktop"
            :ratio-desktop="settings.ratioDesktop"
            :loaded-animation="false"
            :lazyload="banner.lazyload"
            @click="handleAdditionalMediaClick"
            @mouseenter="(e) => handleMediaHover(e, 'additional')"
            @mouseleave="handleMediaLeave"
            @focus="(e) => handleMediaHover(e, 'additional')"
            @blur="handleMediaLeave"
          />
        </template>

        <div
          v-if="banner.enableOverlay"
          class="hero-banner__overlay"
          :style="`background-color: rgba(0,0,0,${banner.overlayRange})`"
        />
      </div>

      <div
        class="hero-banner__content grid"
        :style="{
          '--color-primary': isMobile
            ? settings.headingBackgroundColorMobile
            : settings.headingBackgroundColorDesktop
        }"
      >
        <div
          :class="isMobile
            ? banner.alignVerticalMobile
            : banner.alignVerticalDesktop
          "
        >
          <heading-copy
            :class="isMobile
              ? banner.alignHorizontalMobile
              : banner.alignHorizontalDesktop
            "
            :classes="{
              copy: 'text-body-s-regular text-body-m-regular-desktop',
            }"
            :content="headingContent"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Hero banner (hero-banner)
 * -----------------------------------------------------------------------------
 * Section featuring a full width image or video and overlayed text content.
 * - Dynamic section.
 *
 * @param {Object} banner - Section banner.
 * @param {String} settings.sectionId - Section's Liquid ID.
 * @param {String} settings.ratio - Image's Ratio on Mobile devices.
 * @param {String} settings.ratioDesktop - Image's Ratio on Destkop devices.
 *
 */
import ArtDirection from '~global/images/art-direction'
import HeadingCopy from '~async/heading-copy/heading-copy'
import VideoPlayer from '~async/video-player/video-player'
import { mapState } from 'vuex'

export default {
  name: 'HeroBanner',

  components: {
    ArtDirection,
    HeadingCopy,
    VideoPlayer,
  },

  props: {
    settings: {
      type: Object,
      default: () => ({}),
    },
    banner: {
      type: Object,
      default: () => ({}),
    },
  },

  computed: {
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
    layoutType() {
      return this.isMobile
        ? this.settings.layoutMobile || 'existing_layout'
        : this.settings.layoutDesktop || 'existing_layout'
    },
    layoutClass() {
      return this.isMobile
        ? `layout--mobile ${this.layoutType}`
        : `layout--desktop ${this.layoutType}`
    },

    /**
     * Heading content based on device type (mobile/desktop) set the values
     */
    headingContent() {
      const desc = this.banner.heading || {}
      const isMobile = this.isMobile
      return {
        ...desc,
        textColour: isMobile ? desc.textColourMobile : desc.textColourDesktop,
        buttonText: isMobile ? desc.buttonTextMobile : desc.buttonTextDesktop,
        buttonUrl: isMobile ? desc.buttonUrlMobile : desc.buttonUrlDesktop,
        buttonStyle: isMobile ? desc.buttonStyleMobile : desc.buttonStyleDesktop,
        buttonMode: isMobile ? desc.buttonModeMobile : desc.buttonModeDesktop,
        secondButtonText: isMobile ? desc.secondButtonTextMobile : desc.secondButtonTextDesktop,
        secondButtonUrl: isMobile ? desc.secondButtonUrlMobile : desc.secondButtonUrlDesktop,
        secondButtonStyle: isMobile ? desc.secondButtonStyleMobile : desc.secondButtonStyleDesktop,
        secondButtonMode: isMobile ? desc.secondButtonModeMobile : desc.secondButtonModeDesktop,
      }
    },
  },
  methods: {

    /**
     * Handle mouse enter/focus for media elements
     * @param {Event} event - Mouse/Focus event
     * @param {string} mediaType - 'main' or 'additional'
     */
    handleMediaHover(event, mediaType) {
      // Ignore if hovering over video controls
      if (
        event.target.closest('.video-player__controls') ||
        event.target.classList.contains('video-player__control')
      ) {
        return
      }

      // For layout_1, only show pointer if media has its own link
      if (this.layoutType === 'layout_1') {
        const hasLink = mediaType === 'main'
          ? this.banner.heading?.mainMediaLink
          : this.banner.heading?.additionalMediaLink
        event.target.style.cursor = hasLink ? 'pointer' : 'default'
        return
      }

      // For other layouts, show pointer if heading link exists
      event.target.style.cursor = this.banner.heading?.headingLink ? 'pointer' : 'default'
    },

    /**
     * Handle mouse leave/blur for media elements
     * @param {Event} event - Mouse/Blur event
     */
    handleMediaLeave(event) {
      event.target.style.cursor = 'default'
    },

    /**
     * Handle mouse enter/focus for the container (non-media area)
     * @param {Event} event - Mouse/Focus event
     */
    handleContainerHover(event) {
      // Only apply pointer if not hovering over a media element
      if (
        this.layoutType === 'layout_1' &&
        event.target.closest(
          '.hero-banner__mobile-video, .hero-banner__desktop-video, .hero-banner__mobile-image, .hero-banner__desktop-image, .hero-banner__additional-mobile-video, .hero-banner__additional-desktop-video, .hero-banner__additional-mobile-image, .hero-banner__additional-desktop-image',
        )
      ) {
        event.target.style.cursor = 'default'
        return
      }
      // For layout_1, pointer only if headingLink exists
      // For other layouts, pointer if headingLink exists
      event.target.style.cursor = this.banner.heading?.headingLink ? 'pointer' : 'default'
    },

    /**
     * Handle mouse leave/blur for the container
     * @param {Event} event - Mouse/Blur event
     */
    handleContainerLeave(event) {
      event.target.style.cursor = 'default'
    },

    /**
     * Handle banner click - used for whole banner clickable area
     * but excludes clicks on video/image and video controls on layout_1
     * @param {Event} event - Click event
     */
    handleBannerClick(event) {
      if (
        this.layoutType === 'layout_1' &&
        event.target.closest(
          '.hero-banner__mobile-video, .hero-banner__desktop-video,.hero-banner__mobile-image, .hero-banner__desktop-image, .hero-banner__additional-mobile-video, .hero-banner__additional-desktop-video, .hero-banner__additional-mobile-image, .hero-banner__additional-desktop-image',
        )
      ) {
        return
      }
      if (
        event.target.closest('.video-player__controls') ||
        event.target.classList.contains('video-player__control')
      ) {
        return
      }
      if (this.banner.heading.headingLink) {
        window.location.href = this.banner.heading.headingLink
      }
    },

    /**
     * Handle main media click - used for clicks on video/image on layout_1
     * but excludes clicks on video controls
     * @param {Event} event - Click event
     */
    handleMainMediaClick(event) {
      if (
        event.target.closest('.video-player__controls') ||
        event.target.classList.contains('video-player__control')
      ) {
        return
      }

      if (this.layoutType === 'layout_1' && this.banner.heading.mainMediaLink) {
        window.location.href = this.banner.heading.mainMediaLink
      }
    },

    /**
     * Handle additional media click - used for clicks on additional media but
     *  excludes clicks on video controls
     * @param {Event} event - Click event
     */
    handleAdditionalMediaClick(event) {
      if (
        event.target.closest('.video-player__controls') ||
        event.target.classList.contains('video-player__control')
      ) {
        return
      }
      if (this.layoutType === 'layout_1' && this.banner.heading.additionalMediaLink) {
        window.location.href = this.banner.heading.additionalMediaLink
      }
    },
  },
}
</script>

<style lang="scss">
@import './hero-banner';
@import '../video-player/video-player';
</style>
